import { API_BASE_URL } from "../../../config";
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = `${API_BASE_URL}/auth/api/main`;

const UserRegister = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [nameuser, setNameuser] = useState("");
    // role selection via fetched roles; keep legacy roleName for fallback
    const [roleName, setRoleName] = useState("DEFAULT"); // DEFAULT => backend assigns USER
    const [roleUid, setRoleUid] = useState(""); // if provided, takes precedence

    const [availableRoles, setAvailableRoles] = useState([]); // [{uid, id, name, extra}]
    const [loadingRoles, setLoadingRoles] = useState(false);

    const [availableTags, setAvailableTags] = useState([]); // [{uid, tagname}]
    const [selectedTagUids, setSelectedTagUids] = useState([]);
    const [newTagNames, setNewTagNames] = useState(""); // comma separated

    const [createTagName, setCreateTagName] = useState("");

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [userPermissions, setUserPermissions] = useState([]);

    const navigate = useNavigate();

    const checkToken = useCallback(() => {
        const user = localStorage.getItem("user");
        if (!user) {
            navigate("/login");
            return null;
        }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const authHeaders = (token) => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    });

    // Permissions helpers similar to Home
    const loadPermissionsFromStorage = useCallback(() => {
        const stored = localStorage.getItem("user");
        if (!stored) return [];
        try {
            const user = JSON.parse(stored);
            return Array.isArray(user?.permissions) ? user.permissions : [];
        } catch {
            return [];
        }
    }, []);

    const hasPermission = useCallback((perm) => {
        return userPermissions.includes(perm);
    }, [userPermissions]);

    // On mount, get permissions and ensure access to this page (mod:create)
    useEffect(() => {
        const perms = loadPermissionsFromStorage();
        setUserPermissions(perms);
        if (!perms.includes('user:read')) {
            // no permission to create user, go back to user-management
            navigate('/user-management');
        }
    }, [loadPermissionsFromStorage, navigate]);

    // Load roles dynamically according to permissions
    useEffect(() => {
        const loadRoles = async () => {
            const token = checkToken();
            if (!token) return;
            setLoadingRoles(true);
            try {
                // If user has user:update, they can see same-or-lower, else strictly lower
                const endpoint = hasPermission('user:read') ? `${API_BASE}/roles/same-or-lower` : `${API_BASE}/roles`;
                const resp = await fetch(endpoint, { headers: authHeaders(token) });
                if (resp.ok) {
                    const data = await resp.json();
                    const raw = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                    const normalized = raw
                        .map(r => ({
                            uid: r.uid || r.roleUid || r.id || r.role_id,
                            id: r.id || r.uid || null,
                            name: r.name || r.roleName || r.role_name || '' ,
                            extra: r.extra !== undefined ? r.extra : null
                        }))
                        .filter(r => r.uid && r.name);
                    setAvailableRoles(normalized);
                } else if (resp.status === 403) {
                    // ignore, will fallback to Default only
                    setAvailableRoles([]);
                }
            } catch (e) {
                console.error('Fetch roles error', e);
            } finally {
                setLoadingRoles(false);
            }
        };
        loadRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPermissions]);

    // Load tags for dynamic select
    useEffect(() => {
        const loadTags = async () => {
            const token = checkToken();
            if (!token) return;
            try {
                const resp = await fetch(`${API_BASE}/tags`, { headers: authHeaders(token) });
                if (resp.ok) {
                    const data = await resp.json();
                    const raw = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                    const normalized = raw
                        .map(t => ({
                            uid: t.uid || t.id || t.tagUid || t.tag_id,
                            tagname: t.tagname || t.name || t.tag_name
                        }))
                        .filter(t => t.uid && t.tagname);
                    setAvailableTags(normalized);
                }
            } catch (e) {
                console.error("Fetch tags error", e);
            }
        };
        loadTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateTag = async () => {
        setError("");
        const token = checkToken();
        if (!token) return;
        const tagname = createTagName.trim();
        if (!tagname) return;
        try {
            const resp = await fetch(`${API_BASE}/tags`, {
                method: 'POST',
                headers: authHeaders(token),
                body: JSON.stringify({ tagname })
            });
            if (resp.ok) {
                const created = await resp.json();
                const normalized = {
                    tagname: created.tagname || created.name || created.tag_name || createTagName.trim()
                };
                setAvailableTags(prev => [...prev, normalized]);
                setCreateTagName("");
            } else {
                const err = await resp.text();
                setError(err || 'Failed to create tag');
            }
        } catch (e) {
            console.error('Create tag error', e);
            setError('An error occurred while creating tag');
        }
    };

    // Handle registration with new backend
    const handleRegister = async () => {
        setError("");
        setSuccess("");

        if (!username || !password || !confirmPassword) {
            setError("Username and passwords are required.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        const token = checkToken();
        if (!token) return;

        const payload = {
            username,
            password,
            nameuser: nameuser || undefined,
            // roleUid takes precedence if provided
            roleUid: roleUid || undefined,
            roleName: roleUid ? undefined : (roleName !== 'DEFAULT' ? roleName : undefined),
            tagUids: selectedTagUids.length ? selectedTagUids : undefined,
            tagNames: newTagNames
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .slice(0, 50) || undefined,
        };

        try {
            const response = await fetch(`${API_BASE}/register`, {
                method: "POST",
                headers: authHeaders(token),
                body: JSON.stringify(payload)
            });

            const contentType = response.headers.get('Content-Type') || '';

            if (response.ok) {
                let data = null;
                if (contentType.includes('application/json')) data = await response.json();
                setSuccess("User registration successful!");
                // Optionally show returned uid
                if (data && data.uid) {
                    setSuccess(`User registered. UID: ${data.uid}`);
                }
                // Clear form
                setUsername("");
                setPassword("");
                setConfirmPassword("");
                setNameuser("");
                setRoleName("DEFAULT");
                setRoleUid("");
                setSelectedTagUids([]);
                setNewTagNames("");
            } else {
                if (contentType.includes('application/json')) {
                    const err = await response.json();
                    setError(err.message || 'Registration failed.');
                } else {
                    const txt = await response.text();
                    setError(txt || 'Registration failed.');
                }
            }
        } catch (error) {
            console.error("Registration error:", error);
            setError("An error occurred. Please try again.");
        }
    };

    return (
        <div className="btc-register-card">
            <div className="btc-register-card-head">
                <button type="button" onClick={() => navigate('/user-management')} className="btc-register-back">
                    ← กลับ
                </button>
                <div className="btc-register-title-block">
                    <h1>สร้างผู้ใช้งานใหม่</h1>
                    <p>User Register · กำหนดบัญชี บทบาท และแท็กสำหรับผู้ใช้งาน</p>
                </div>
            </div>

            <div className="btc-register-form">
                {error && (
                    <div className="btc-register-alert btc-register-alert-error">
                        <span className="btc-register-alert-icon">!</span>
                        <div>
                            <strong>ไม่สามารถบันทึกผู้ใช้ได้</strong>
                            {error}
                        </div>
                    </div>
                )}
                {success && (
                    <div className="btc-register-alert btc-register-alert-success">
                        <span className="btc-register-alert-icon">✓</span>
                        <div>
                            <strong>สำเร็จ</strong>
                            {success}
                        </div>
                    </div>
                )}

                <section className="btc-register-section">
                    <h2 className="btc-register-section-title">Account <small>ข้อมูลสำหรับเข้าสู่ระบบ</small></h2>
                    <div className="btc-register-field">
                        <label>Username</label>
                        <input className="btc-register-input" type="text" value={username} placeholder="Username" onChange={e => setUsername(e.target.value)} />
                    </div>
                    <div className="btc-register-field">
                        <label>Password</label>
                        <div className="btc-register-input-wrap">
                            <input
                                className="btc-register-input"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                placeholder="Password"
                                onChange={e => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="btc-register-show-pass"
                                onClick={() => setShowPassword(prev => !prev)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? "ซ่อน" : "แสดง"}
                            </button>
                        </div>
                    </div>
                    <div className="btc-register-field">
                        <label>Confirm Password</label>
                        <div className="btc-register-input-wrap">
                            <input
                                className="btc-register-input"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                placeholder="Confirm Password"
                                onChange={e => setConfirmPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="btc-register-show-pass"
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? "ซ่อน" : "แสดง"}
                            </button>
                        </div>
                    </div>
                    <div className="btc-register-field">
                        <label>Display Name <span className="btc-register-optional">(optional)</span></label>
                        <input className="btc-register-input" type="text" value={nameuser} placeholder="Display Name (nameuser)" onChange={e => setNameuser(e.target.value)} />
                    </div>
                </section>

                <section className="btc-register-section">
                    <h2 className="btc-register-section-title">Role <small>กำหนดระดับการเข้าถึง</small></h2>
                    <div className="btc-register-field">
                        <label>Role</label>
                        <select
                            className="btc-register-input"
                            value={roleUid || 'DEFAULT'}
                            onChange={e => {
                                const v = e.target.value;
                                if (v === 'DEFAULT') {
                                    setRoleUid('');
                                    setRoleName('DEFAULT');
                                } else {
                                    setRoleUid(v);
                                    setRoleName(''); // when uid chosen, name should be ignored
                                }
                            }}
                            disabled={loadingRoles}
                        >
                            <option value="DEFAULT">Default (USER)</option>
                            {availableRoles.map(r => (
                                <option key={r.uid} value={r.uid}>
                                    {r.name}
                                </option>
                            ))}
                        </select>
                        {loadingRoles && <p className="btc-register-status">Loading roles...</p>}
                        {!loadingRoles && availableRoles.length === 0 && (
                            <p className="btc-register-status">No roles available or missing permission.</p>
                        )}
                    </div>
                </section>

                <section className="btc-register-section">
                    <h2 className="btc-register-section-title">Tags <small>เลือกแท็กที่เกี่ยวข้องกับผู้ใช้งาน</small></h2>
                    <div className="btc-register-chips" aria-label="เลือกแท็ก">
                        {availableTags.map(t => {
                            const isSelected = selectedTagUids.includes(t.uid);
                            return (
                                <button
                                    key={t.uid}
                                    type="button"
                                    className={`btc-tag-chip ${isSelected ? 'selected' : ''}`}
                                    onClick={() => {
                                        setSelectedTagUids(prev =>
                                            isSelected ? prev.filter(uid => uid !== t.uid) : [...prev, t.uid]
                                        );
                                    }}
                                >
                                    {isSelected ? '✓ ' : ''}{t.tagname}
                                </button>
                            );
                        })}
                    </div>
                    <div className="btc-register-field">
                        <label>New tag names <span className="btc-register-optional">(comma separated)</span></label>
                        <input className="btc-register-input" type="text" value={newTagNames} placeholder="New tag names (comma separated)" onChange={e => setNewTagNames(e.target.value)} />
                    </div>
                    <div className="btc-register-field">
                        <label>Create one tag</label>
                        <div className="btc-register-inline-create">
                            <input className="btc-register-input" type="text" value={createTagName} placeholder="Create one tag" onChange={e => setCreateTagName(e.target.value)} />
                            <button type="button" className="btc-register-small-button" onClick={handleCreateTag}>Create Tag</button>
                        </div>
                    </div>
                </section>

                <div className="btc-register-footer">
                    <button className="btc-register-save" onClick={handleRegister}>บันทึก</button>
                </div>
            </div>
        </div>
    );
};

export default UserRegister;
